import { useCallback, useEffect, useState, type ReactNode } from "react";

import { formatMoney } from "../money.ts";
import type { Refund as RefundRecord, RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  EmptyRow,
  Group,
  InfoNote,
  MarketShell,
  type BadgeValue,
  type StepsValue,
} from "./MarketShell.tsx";
import { Absent } from "./PhoneShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Refund` — FIN-04's refund, and the one screen where this build does **not** read the listing the
 * measurement names.
 *
 * | Drawn                                   | From                                                |
 * | --------------------------------------- | --------------------------------------------------- |
 * | `KES 30 on its way`                     | FIN-04's `amountMinor` and `currency`               |
 * | `processing`                            | FIN-04's `status`                                   |
 * | `For 1 bunch of sukuma wiki not packed` | FIN-04's `reasonCode`, which is a code and not prose |
 * | `Order co_7Q2K`                         | **nothing** — FIN-04 publishes a `paymentIntentId`  |
 * | the three timeline rows                 | **nothing** — no refund timeline is published       |
 * | `Refunds are added as new entries…`     | designed copy, and a platform rule besides          |
 *
 * ## Why the listing is not read, although the measurement counts it as a source
 *
 * The measurement names `GET /v1/refunds` (FIN-04) for _"this customer's refunds, so one can be
 * opened at all"_, and that operation exists and is public. **It has no customer filter.** Its query
 * parameters are `status`, `paymentIntentId`, `limit` and `after`, so a rider application calling it
 * would be handed **every refund in the tenant** — other people's money, on a phone, under
 * `refund:read`. `AGENTS.md`: never expose one tenant's data to another, and the same reasoning
 * applies a level down to one customer's data against another's inside a tenant.
 *
 * So this screen is addressed by its own `refundId`, exactly as the seven journey screens are
 * addressed by a ride request, and the element the measurement grades present is drawn as the
 * absence it is in practice. That is a finding rather than a workaround and it is recorded as one:
 * **FIN-04's listing needs a payer or customer filter before any customer-facing screen can use
 * it.**
 *
 * ## The step bar draws the wrong four words, and they are copied anyway
 *
 * `14 WP13 Money and Account.dc.html` sets `refund:{…steps:1…}` and takes its labels from the same
 * hard-coded `['Accepted','Preparing','Ready','On the way']` that `Order tracking` uses — it is one
 * array in `renderVals`, outside both screens' data. So the design draws a refund's progress with an
 * **order preparation's** step words.
 *
 * `CLAUDE.md` section 1 is explicit about this case: _"If the design is ambiguous or seems wrong,
 * stop and ask the user. Do not decide yourself, and do not 'fix' it."_ So the four words are drawn
 * as written and the question is recorded rather than answered here. It is not blocking — the screen
 * is `degraded` either way and the three timeline rows below the bar carry the refund's real
 * progress — but it is the one place in this tranche where the build is knowingly drawing something
 * that reads wrong, and the step bar's own position is taken from FIN-04's status rather than from
 * the labels.
 */

export interface RefundProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly refundId: string | undefined;
  readonly onBack: () => void;
}

/** The design's own four, hard-coded in `renderVals` and shared with `Order tracking`. */
const STEP_LABELS: readonly [string, string, string, string] = [
  "Accepted",
  "Preparing",
  "Ready",
  "On the way",
];

/**
 * FIN-04's six statuses against the step bar, the chip's kind, and the card's title.
 *
 * One table rather than three, because the three must agree: a refund drawn as `SUCCEEDED` in the
 * chip and stopped at the second step would be two answers to one question. The design draws
 * `PROCESSING` only; the `title` for the other five is this build's and is flagged on screen.
 */
const BY_STATUS: Readonly<
  Record<RefundRecord["status"], { step: number; kind: BadgeValue["kind"]; title: string }>
> = {
  REQUESTED: { step: 0, kind: "idle", title: "%s requested" },
  PROCESSING: { step: 1, kind: "active", title: "%s on its way" },
  SUCCEEDED: { step: 3, kind: "done", title: "%s is back in your account" },
  FAILED: { step: 1, kind: "caution", title: "%s could not be refunded" },
  CANCELLED: { step: 0, kind: "closed", title: "%s refund was cancelled" },
  /*
   * The sixth, and the one FIN-04 publishes that the design has no state for.
   *
   * `OUTCOME_UNKNOWN` is the refund-side twin of the whole of `07 Payment Outcome Unknown`: the
   * provider has not said. It takes the `unknown` kind — the dashed pill with a literal `?`, which
   * design rule 3.2 reserves for exactly this — rather than `PROCESSING`'s confident blue.
   */
  OUTCOME_UNKNOWN: { step: 1, kind: "unknown", title: "%s — nobody has confirmed this yet" },
};

export function Refund(props: RefundProps): ReactNode {
  const { api, refundId } = props;

  const [refund, setRefund] = useState<RefundRecord | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    if (refundId === undefined) return;
    setState({ kind: "loading" });
    api
      .refund(refundId)
      .then((answer) => {
        setRefund(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.refunds])));
  }, [api, refundId]);

  useEffect(load, [load]);

  if (refundId === undefined) {
    return (
      <MarketState
        state={{
          kind: "error",
          code: "no_refund_in_address",
          message:
            "This screen is about one refund and the address does not name one. GET /v1/refunds (FIN-04) has no customer filter, so listing them here would show this rider other people's money.",
          retryable: false,
          conflict: false,
        }}
        onBack={props.onBack}
        purpose="Reading your refund."
        barTitle="Refund"
        barSub="CityCab"
      />
    );
  }

  if (state.kind !== "ready" || refund === undefined) {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading your refund."
        barTitle="Refund"
        barSub="CityCab"
      />
    );
  }

  const drawn = BY_STATUS[refund.status];
  const amount = formatMoney(refund.amountMinor, refund.currency);
  const steps: StepsValue = { labels: STEP_LABELS, current: drawn.step };

  return (
    <MarketShell
      screen="refund"
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle="Refund"
      barSub={
        <Absent words="No order: FIN-04 publishes the paymentIntentId a refund points at, not the order behind it" />
      }
      right={{
        glyph: "support_agent",
        label: "Support",
        unavailable:
          "SUP-01 publishes only /internal/v1 operations, which the gateway does not expose",
      }}
      badge={{ kind: drawn.kind, label: refund.status.toLowerCase().replaceAll("_", " ") }}
      title={drawn.title.replace("%s", amount)}
      /*
       * `For 1 bunch of sukuma wiki not packed` — what the refund is for.
       *
       * FIN-04 publishes `reasonCode` and nothing else about why: a free string with no vocabulary,
       * which is the same shape as MOB-03's cancel reason and RAT-01's rule version. It is drawn
       * verbatim rather than translated, because translating a code this application does not own
       * the vocabulary of is how a wrong sentence gets a confident voice.
       */
      sub={refund.reasonCode}
      {...(refund.status === "PROCESSING"
        ? {}
        : { flag: "the title is this build's wording: the design draws PROCESSING only" })}
      steps={steps}
    >
      <Group
        rows={[]}
        empty={
          <EmptyRow words="No timeline: FIN-04 publishes requestedAt, updatedAt and completedAt on the refund and no sequence of events, so `Refund requested · 12:47 · by the store` has no source" />
        }
      />
      <InfoNote text="Refunds are added as new entries. Your original payment stays on record unchanged." />
    </MarketShell>
  );
}
